import { Accordion, Section } from '@/components/Accordion';
import { Portal } from '@/components/Portal';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem2 } from '@/components/ui/radio-group';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { getCountries } from '@/util/get-countries';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { X as CloseIcon } from 'react-feather';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { LinkButton } from '../ui/link-button';
import { useWarrantyPage } from './hooks/use-warranty-page';
import type { ResponseRoot } from './types/response';
import classes from './warranty-registeration-form.module.css';

interface OrderFormatType {
  [key: string]: {
    placeholder: string;
    pattern: string;
    example: string;
  };
}

const orderFormats: OrderFormatType = {
  '1': {
    // Amazon
    placeholder: '',
    pattern: '^\\d{3}-\\d{7}-\\d{7}$',
    example: 'Wrong Format, Example: 113-4028907-5109868',
  },
  '2': {
    // eBay
    placeholder: '',
    pattern: '^\\d{2}-\\d{5}-\\d{5}$',
    example: 'Wrong Format, Example: 18-12461-92118',
  },
  '3': {
    // hisea.com
    placeholder: '',
    pattern: '^(HSUS|HSDE)\\d{9,}$',
    example: 'Wrong Format, Example: HSUS2411229478',
  },
  '4': {
    // Other
    placeholder: '',
    pattern: '.+',
    example: '',
  },
  '5': {
    // Walmart
    placeholder: '',
    pattern: '^\\d{15}$',
    example: 'Wrong Format, Example: 200012461482907',
  },
  '6': {
    // tiktok
    placeholder: '',
    pattern: '^\\d{18}$',
    example: 'Wrong Format, Example: 576838957484380219',
  },
  '7': {
    // Dealer
    placeholder: '',
    pattern: '.*', // accept anything or nothing since it's hidden
    example: '',
  },
};

// 判断日期是否合法
const judgeData = (y: number, m: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12, d: number) => {
  if (y >= 1923 && y <= 3000 && y % 1 === 0) {
    // 判断月份
    if (m >= 1 && m <= 12 && m % 1 === 0) {
      // 判断日期 ：注意具体分析2月份是否为闰年，令每个月最大的天数为maxDay
      let maxDay: 31 | 30 | 29 | 28 = 30;

      if (m === 1 || m === 3 || m === 5 || m === 7 || m === 8 || m === 10 || m === 12) {
        maxDay = 31;
      } else if (m === 4 || m === 6 || m === 9 || m === 11) {
        maxDay = 30;
      } else if (m === 2) {
        // 判断年份是否为闰年，从而判断二月天数
        if ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) {
          maxDay = 29;
        } else {
          maxDay = 28;
        }
      }

      // 判断日期是否合法
      if (d >= 1 && d <= maxDay && d % 1 === 0) {
        return true;
      }
    }
  }

  return false;
};

// Define form validation schema
const formSchema = z
  .object({
    email: z.string().email(),
    firstname: z.string().min(1, 'First name is required'),
    lastname: z.string().min(1, 'Last name is required'),
    phone: z.string().min(1, 'Phone number is required'),
    birthday: z.string().min(1, 'Birthday is required'),
    gender: z.enum(['1', '2', '3']),
    purchased_item_category: z.enum(['1', '2', '3']),
    usage: z.enum(['1', '2', '3', '4', '5']),
    platform: z.enum(['1', '2', '3', '4', '5', '6']),
    platform_other: z.string().optional(),

    website: z.enum(['1', '2', '3', '4', '5', '6', '7']),
    website_other: z.string().optional(),
    order_number: z.string().optional(),
    country_id: z.string().optional(),
  })
  .refine(
    ({ website, order_number }) => {
      if (website !== '7' && (!order_number || order_number.trim() === '')) {
        return false;
      }
      return true;
    },
    {
      message: 'Order number is required',
      path: ['order_number'],
    },
  )
  .refine(
    ({ website, order_number }) => {
      if (website === '1' && !(order_number || '').match(orderFormats['1'].pattern)) {
        return false;
      }

      return true;
    },
    {
      message: orderFormats['1'].example,
      path: ['order_number'],
    },
  )
  .refine(
    ({ website, order_number }) => {
      if (website === '2' && !(order_number || '').match(orderFormats['2'].pattern)) {
        return false;
      }

      return true;
    },
    {
      message: orderFormats['2'].example,
      path: ['order_number'],
    },
  )
  .refine(
    ({ website, order_number }) => {
      if (website === '3' && !(order_number || '').match(orderFormats['3'].pattern)) {
        return false;
      }

      return true;
    },
    {
      message: orderFormats['3'].example,
      path: ['order_number'],
    },
  )
  .refine(
    ({ website, order_number }) => {
      if (website === '4' && !(order_number || '').match(orderFormats['4'].pattern)) {
        return false;
      }

      return true;
    },
    {
      message: orderFormats['4'].example,
      path: ['order_number'],
    },
  )
  .refine(
    ({ website, order_number }) => {
      if (website === '5' && !(order_number || '').match(orderFormats['5'].pattern)) {
        return false;
      }

      return true;
    },
    {
      message: orderFormats['5'].example,
      path: ['order_number'],
    },
  )
  .refine(
    ({ website, order_number }) => {
      if (website === '6' && !(order_number || '').match(orderFormats['6'].pattern)) {
        return false;
      }

      return true;
    },
    {
      message: orderFormats['6'].example,
      path: ['order_number'],
    },
  )
  .refine(
    ({ birthday }) => {
      const [month, day, year] = birthday.split('/').map((num: string) => parseInt(num));

      // Validate date before submission
      if (!judgeData(year, month as any, day)) {
        return false;
      }

      return true;
    },
    {
      message: 'Please enter a valid date',
      path: ['birthday'],
    },
  );

export type FormSchema = z.infer<typeof formSchema>;

export const WarrantyRegisterationForm = () => {
  // 修改 input 基础样式类，添加错误状态样式
  const getInputClasses = (hasError: boolean) =>
    cn(
      'border-1 border-solid border-[#E0E0E0] focus_border-[#E0E0E0]',
      // 当有错误时添加红色边框
      hasError && 'border-1 focus_border-[#FF0000] border-solid border-[#FF0000]',
    );

  const labelClasses = 'text-[16px] font-roboto-light font-light';
  const bigLabelClasses = 'lg_text-[20px] lg_leading-[28px] text-[16px] font-roboto-medium mb-[20px]';
  const [openMaskDialog, setOpenMaskDialog] = useState(false);
  const [responseInfo, setResponseInfo] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const countries = useMemo(() => getCountries(), []);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema as any),
    defaultValues: {
      email: '',
      firstname: '',
      lastname: '',
      phone: '',
      birthday: '',
      gender: undefined,
      purchased_item_category: undefined,
      usage: undefined,
      platform: undefined,
      platform_other: '',

      website: undefined,
      website_other: '',
      order_number: '',
      country_id: '',
    },
  });

  const { isLoading, handleSubmit } = useWarrantyPage({
    afterSubmit: ({ response }: { response: ResponseRoot }) => {
      if (response?.data?.addWarrantyInfo?.code === '200') {
        setResponseInfo(response?.data?.addWarrantyInfo?.content);
        setOpenMaskDialog(true);
        setSubmitSuccess(true);
      } else {
        setResponseInfo(response?.data?.addWarrantyInfo?.content);
        setOpenMaskDialog(true);
        setSubmitSuccess(false);
      }
    },
  });

  const onSubmit = (values: FormSchema) => {
    // Extract date components from birthday string (MM/DD/YYYY format)
    // const [month, day, year] = values.birthday.split("/").map(num => parseInt(num));

    // // Validate date before submission
    // if (!judgeData(year, month as any, day)) {
    //   form.setError("birthday", {
    //     type: "manual",
    //     message: "Please enter a valid date"
    //   });

    //   return;
    // }

    handleSubmit(values);
  };
  const { isMobile } = useIsMobile();
  return (
    <div className='personalContent mx-auto mb-[40px] lg_mb-[80px]'>
      <h2
        id='warranty-registeration'
        className='mb-[10px] px-[12px] font-teko-medium text-[28px] uppercase leading-[30px] lg_px-0 lg_text-[40px] lg_leading-[40px]'
      >
        Warranty Registration
      </h2>

      <h3 className='mb-[20px] px-[12px] font-roboto-regular text-[16px] font-light leading-[24px] lg_mb-[40px] lg_px-0 lg_text-[18px] lg_leading-[30px]'>
        To enjoy the full benefits of our Lifetime Warranty, please register your HISEA product. Registration helps us
        assist you more quickly with any claims, replacements, or support.
      </h3>
      {isMobile ? (
        <div className='px-3'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className=' '
            >
              <Accordion
                canOpenMultiple
                classes={{
                  root: 'border-t-[1px] border-[#E0E0E0]',
                }}
              >
                <Section
                  classes={{
                    root: 'border-b-0 border-b-1 border-[#E0E0E0]',
                    title: 'font-roboto-medium text-[18px] font-medium capitalize leading-[30px]',
                    title_container: 'block ursor-pointer w-full border-none lg_py-[20px] py-[17px]',
                    contents_container: 'py-0 my-0',
                  }}
                  id={'Contact Information'}
                  title={'Contact Information (1/3)'}
                  isOpen
                >
                  <div className='pb-5'>
                    <FormField
                      control={form.control}
                      name='email'
                      render={({ field, formState }) => (
                        <FormItem className={classes.formItem}>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Email</FormLabel>
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.email)}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='firstname'
                      render={({ field, formState }) => (
                        <FormItem className={classes.formItem}>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>First Name</FormLabel>
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.firstname)}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='lastname'
                      render={({ field, formState }) => (
                        <FormItem className={classes.formItem}>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Last Name</FormLabel>
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.lastname)}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='phone'
                      render={({ field, formState }) => (
                        <FormItem className={classes.formItem}>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Phone Number</FormLabel>
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.phone)}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='birthday'
                      render={({ field, formState }) => (
                        <FormItem className={classes.formItem}>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Birthday</FormLabel>{' '}
                          <FormMessage />
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.birthday)}
                              placeholder='MM / DD / YYYY'
                              onChange={e => {
                                let value = e.target.value.replace(/\D/g, '');

                                if (value.length > 2) {
                                  value = value.slice(0, 2) + '/' + value.slice(2);
                                }

                                if (value.length > 5) {
                                  value = value.slice(0, 5) + '/' + value.slice(5);
                                }

                                if (value.length > 10) {
                                  value = value.slice(0, 10);
                                }

                                field.onChange(value);
                              }}
                              value={field.value}
                              maxLength={10}
                            />
                          </FormControl>
                          <p className='font-roboto-regular text-[14px] text-[#4C4C4C]'>
                            Note: We offer an exclusive birthday discount for HISEA members.
                          </p>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='country_id'
                      render={({ field, formState }) => (
                        <FormItem className={classes.formItem}>
                          <FormLabel className={cn(labelClasses, 'font-roboto-regular font-normal')}>Country</FormLabel>
                          <FormControl>
                            <div className='relative'>
                              <select
                                className={cn(
                                  getInputClasses(!!formState.errors.country_id),
                                  'duration-300 flex h-[50px] w-full appearance-none border-1 border-solid border-[#E0E0E0] bg-white px-3 py-2 pr-10 text-sm outline-none transition-all focus_border-[#E0E0E0]',
                                )}
                                {...field}
                              >
                                <option value=''>Select a country</option>
                                {countries.map((country: any) => (
                                  <option
                                    value={country.two_letter_abbreviation}
                                    key={country.id}
                                  >
                                    {country.full_name_english}
                                  </option>
                                ))}
                              </select>
                              <div className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'>
                                <svg
                                  width='12'
                                  height='8'
                                  viewBox='0 0 12 8'
                                  fill='none'
                                >
                                  <path
                                    d='M1 1.5L6 6.5L11 1.5'
                                    stroke='#333'
                                    strokeWidth='1.5'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                              </div>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='gender'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel, 'mb-5')}>Gender</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className='flex flex-col justify-start gap-5'
                            >
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='1'
                                  id='gender_1'
                                />
                                <label
                                  className={labelClasses}
                                  htmlFor='gender_1'
                                >
                                  Male
                                </label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='2'
                                  id='gender_2'
                                />
                                <label
                                  className={labelClasses}
                                  htmlFor='gender_2'
                                >
                                  Female
                                </label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='3'
                                  id='gender_3'
                                />
                                <label
                                  className={labelClasses}
                                  htmlFor='gender_3'
                                >
                                  Prefer Not to Say
                                </label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Section>
              </Accordion>
              <Accordion
                canOpenMultiple
                classes={{
                  root: '',
                }}
              >
                <Section
                  classes={{
                    root: 'border-b-0 border-b-1 border-[#E0E0E0]',
                    title: 'font-roboto-medium text-[18px] font-medium capitalize leading-[30px]',
                    title_container: 'block ursor-pointer w-full border-none lg_py-[20px] py-[17px]',
                    contents_container: 'py-0 my-0',
                  }}
                  id={'Order Information'}
                  title={'Order Information (2/3)'}
                  isOpen
                >
                  <div className='pb-5'>
                    <FormField
                      control={form.control}
                      name='website'
                      render={({ field }) => (
                        <FormItem className='mb-[20px]'>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel, 'mb-[25px]')}>
                            Where did you buy our product?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={value => {
                                field.onChange(value);
                                // handleWebsiteSelection(value);
                              }}
                              className='flex h-fit flex-col items-start gap-4'
                              value={field.value}
                            >
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='1'
                                  id='website_1'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='website_1'
                                >
                                  Amazon
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='2'
                                  id='website_2'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='website_2'
                                >
                                  eBay
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='3'
                                  id='website_3'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='website_3'
                                >
                                  hisea.com
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='5'
                                  id='website_5'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='website_5'
                                >
                                  Walmart
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='6'
                                  id='website_6'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='website_6'
                                >
                                  TikTok Shop
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='7'
                                  id='website_7'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='website_7'
                                >
                                  Dealer
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='4'
                                  id='website_4'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='website_4'
                                >
                                  Other
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='website_other'
                      render={({ field, formState }) => (
                        <FormItem className={cn(form.watch('website') === '4' ? 'mb-[20px] block ' : 'hidden')}>
                          <FormLabel
                            className={cn(
                              labelClasses,
                              classes.requiredLabel,
                              // 'flex flex-col items-center gap-[10px] lg_flex-row',
                            )}
                          >
                            <div>Please specify the place you bought our products</div>
                          </FormLabel>
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.website)}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='order_number'
                      render={({ field, formState }) => (
                        <FormItem className={cn('mb-[20px]', form.watch('website') === '7' ? 'hidden' : '')}>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Order Number</FormLabel>
                          <div className='h-4 w-full truncate font-roboto-regular text-[14px] leading-4 text-[#4C4C4C]'>
                            (Please enter your order number, not your warranty card number.)
                          </div>
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.order_number)}
                              placeholder={
                                form.watch('website')
                                  ? orderFormats[form.watch('website')].placeholder
                                  : orderFormats['4'].placeholder
                              }
                              {...field}
                              id='order_number'
                              autoComplete='order_number'
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='purchased_item_category'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>
                            Which products did you buy from us?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className='flex h-fit flex-col items-start lg_h-[40px] lg_flex-row lg_items-center lg_gap-[100px]'
                            >
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='1'
                                  id='category_1'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='category_1'
                                >
                                  Boots
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='2'
                                  id='category_2'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='category_2'
                                >
                                  Waders
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Section>
              </Accordion>
              <Accordion
                canOpenMultiple
                classes={{
                  root: '',
                }}
              >
                <Section
                  classes={{
                    root: 'border-b-0 border-b-1 border-[#E0E0E0]',
                    title: 'font-roboto-medium text-[18px] font-medium capitalize leading-[30px] text-left',
                    title_container: 'block ursor-pointer w-full border-none lg_py-[20px] py-[17px]',
                    contents_container: 'py-0 my-0',
                  }}
                  id={'Preferences'}
                  title={'Preferences (3/3)'}
                  isOpen
                >
                  <div className='pb-5'>
                    <FormField
                      control={form.control}
                      name='usage'
                      render={({ field }) => (
                        <FormItem className='mb-[20px]'>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel, 'mb-[25px]')}>
                            What do you mainly use our products for?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className='flex h-fit flex-col items-start gap-5'
                            >
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='1'
                                  id='usage_1'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='usage_1'
                                >
                                  Farming
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='2'
                                  id='usage_2'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='usage_2'
                                >
                                  Gardening
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='3'
                                  id='usage_3'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='usage_3'
                                >
                                  Hunting
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='4'
                                  id='usage_4'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='usage_4'
                                >
                                  Outdoor works
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='5'
                                  id='usage_5'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='usage_5'
                                >
                                  Fishing
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='platform'
                      render={({ field }) => (
                        <FormItem className='mb-[20px]'>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel, 'mb-[25px]')}>
                            Which social platform do you use most?
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={value => {
                                field.onChange(value);
                              }}
                              value={field.value}
                              className='flex h-fit flex-col items-start gap-5'
                            >
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='1'
                                  id='platform_1'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='platform_1'
                                >
                                  Facebook
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='2'
                                  id='platform_2'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='platform_2'
                                >
                                  Instagram
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='3'
                                  id='platform_3'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='platform_3'
                                >
                                  YouTube
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='4'
                                  id='platform_4'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='platform_4'
                                >
                                  Tiktok
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='5'
                                  id='platform_5'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='platform_5'
                                >
                                  Pinterest
                                </Label>
                              </div>
                              <div className='flex items-center space-x-2'>
                                <RadioGroupItem2
                                  value='6'
                                  id='platform_6'
                                />
                                <Label
                                  className={labelClasses}
                                  htmlFor='platform_6'
                                >
                                  Others
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='platform_other'
                      render={({ field, formState }) => (
                        <FormItem className={cn(form.watch('platform') === '6' ? 'block' : 'hidden')}>
                          <FormLabel className={cn(labelClasses, classes.requiredLabel)}>
                            If Others, please specify
                          </FormLabel>
                          <FormControl>
                            <Input
                              className={getInputClasses(!!formState.errors.platform)}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Section>
              </Accordion>
              <div className='mt-[20px]'>
                <Button
                  disabled={isLoading}
                  type='submit'
                  className='duration-300 h-[40px] w-full rounded-full bg-black font-roboto-medium text-[16px] leading-[40px] text-white transition-colors hover_bg-[#4C4C4C] lg_h-[50px] lg_w-[140px] lg_text-[18px] lg_leading-[50px]'
                >
                  {isLoading ? 'loading...' : 'Send'}
                </Button>
                {!form.formState.isValid ? (
                  <p className='mt-[10px] text-center font-roboto-regular text-[14px] font-normal text-[#B23C3C]'>
                    * There is still information left unfilled
                  </p>
                ) : null}
              </div>
            </form>
          </Form>
        </div>
      ) : (
        <div className=''>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className=' '
            >
              <div className='border-b-1 border-solid border-[#E0E0E0] pb-10'>
                <h2 className={cn(bigLabelClasses)}>Contact Information (1/3)</h2>
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field, formState }) => (
                    <FormItem className={classes.formItem}>
                      <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Email</FormLabel>
                      <FormControl>
                        <Input
                          className={getInputClasses(!!formState.errors.email)}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className='mb-[20px] flex w-full flex-col gap-[40px] lg_flex-row'>
                  <FormField
                    control={form.control}
                    name='firstname'
                    render={({ field, formState }) => (
                      <FormItem className={cn('w-full basis-1/2')}>
                        <FormLabel className={cn(labelClasses, classes.requiredLabel)}>First Name</FormLabel>
                        <FormControl>
                          <Input
                            className={getInputClasses(!!formState.errors.firstname)}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='lastname'
                    render={({ field, formState }) => (
                      <FormItem className='w-full basis-1/2'>
                        <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Last Name</FormLabel>
                        <FormControl>
                          <Input
                            className={getInputClasses(!!formState.errors.lastname)}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className='flex w-full flex-col gap-[40px] lg_flex-row'>
                  <FormField
                    control={form.control}
                    name='phone'
                    render={({ field, formState }) => (
                      <FormItem className='w-full basis-1/2'>
                        <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Phone Number</FormLabel>
                        <FormControl>
                          <Input
                            className={getInputClasses(!!formState.errors.phone)}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='birthday'
                    render={({ field, formState }) => (
                      <FormItem className='w-full basis-1/2'>
                        <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Birthday</FormLabel>{' '}
                        <FormControl>
                          <Input
                            className={getInputClasses(!!formState.errors.birthday)}
                            placeholder='MM / DD / YYYY'
                            onChange={e => {
                              let value = e.target.value.replace(/\D/g, '');

                              if (value.length > 2) {
                                value = value.slice(0, 2) + '/' + value.slice(2);
                              }

                              if (value.length > 5) {
                                value = value.slice(0, 5) + '/' + value.slice(5);
                              }

                              if (value.length > 10) {
                                value = value.slice(0, 10);
                              }

                              field.onChange(value);
                            }}
                            value={field.value}
                            maxLength={10}
                          />
                        </FormControl>
                        <FormMessage />
                        <p className='font-roboto-regular text-[14px] text-[#4C4C4C]'>
                          Note: We provide exclusive birthday discount for Hisea members.
                        </p>
                      </FormItem>
                    )}
                  />
                </div>

                <div className='mb-[20px] mt-[20px] flex w-full flex-col gap-[20px] lg_flex-row lg_gap-[40px]'>
                  <FormField
                    control={form.control}
                    name='country_id'
                    render={({ field, formState }) => (
                      <FormItem className='w-full lg_w-1/2'>
                        <FormLabel className={cn(labelClasses, 'font-roboto-regular font-normal')}>Country</FormLabel>
                        <FormControl>
                          <div className='relative'>
                            <select
                              className={cn(
                                getInputClasses(!!formState.errors.country_id),
                                'duration-300 flex h-[50px] w-full appearance-none border-1 border-solid border-[#E0E0E0] bg-white px-3 py-2 pr-10 text-sm outline-none transition-all focus_border-[#E0E0E0]',
                              )}
                              {...field}
                            >
                              <option value=''>Select a country</option>
                              {countries.map((country: any) => (
                                <option
                                  value={country.two_letter_abbreviation}
                                  key={country.id}
                                >
                                  {country.full_name_english}
                                </option>
                              ))}
                            </select>
                            <div className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'>
                              <svg
                                width='12'
                                height='8'
                                viewBox='0 0 12 8'
                                fill='none'
                              >
                                <path
                                  d='M1 1.5L6 6.5L11 1.5'
                                  stroke='#333'
                                  strokeWidth='1.5'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='gender'
                    render={({ field }) => (
                      <FormItem className='w-full lg_w-1/2'>
                        <FormLabel className={cn(labelClasses, classes.requiredLabel)}>Gender</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className='flex h-[50px] items-center gap-[40px]'
                          >
                            <div className='flex items-center space-x-2'>
                              <RadioGroupItem2
                                value='1'
                                id='gender_1'
                              />
                              <label
                                className={labelClasses}
                                htmlFor='gender_1'
                              >
                                Male
                              </label>
                            </div>
                            <div className='flex items-center space-x-2'>
                              <RadioGroupItem2
                                value='2'
                                id='gender_2'
                              />
                              <label
                                className={labelClasses}
                                htmlFor='gender_2'
                              >
                                Female
                              </label>
                            </div>
                            <div className='flex items-center space-x-2'>
                              <RadioGroupItem2
                                value='3'
                                id='gender_3'
                              />
                              <label
                                className={labelClasses}
                                htmlFor='gender_3'
                              >
                                Prefer Not to Say
                              </label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className='border-b-1 border-solid border-[#E0E0E0] pb-10'>
                <h2 className={cn(bigLabelClasses, 'mt-[20px] lg_mt-[40px]')}>Order Information (2/3)</h2>

                <FormField
                  control={form.control}
                  name='website'
                  render={({ field }) => (
                    <FormItem className='mb-[20px]'>
                      <FormLabel className={cn(labelClasses, classes.requiredLabel)}>
                        Where did you buy our product?
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={value => {
                            field.onChange(value);
                            // handleWebsiteSelection(value);
                          }}
                          className='flex h-fit flex-col items-start lg_flex-row lg_flex-wrap lg_items-center lg_gap-x-[60px] lg_gap-y-[10px]'
                          value={field.value}
                        >
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='1'
                              id='website_1'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='website_1'
                            >
                              Amazon
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='2'
                              id='website_2'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='website_2'
                            >
                              eBay
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='3'
                              id='website_3'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='website_3'
                            >
                              hisea.com
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='5'
                              id='website_5'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='website_5'
                            >
                              Walmart
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='6'
                              id='website_6'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='website_6'
                            >
                              TikTok Shop
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='7'
                              id='website_7'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='website_7'
                            >
                              Dealer
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='4'
                              id='website_4'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='website_4'
                            >
                              Other
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='website_other'
                  render={({ field, formState }) => (
                    <FormItem className={cn(form.watch('website') === '4' ? 'mb-[20px] block ' : 'hidden')}>
                      <FormLabel
                        className={cn(
                          labelClasses,
                          classes.requiredLabel,
                          // 'flex flex-col items-center gap-[10px] lg_flex-row',
                        )}
                      >
                        <div>Please specify the place you bought our products</div>
                      </FormLabel>
                      <FormControl>
                        <Input
                          className={cn(getInputClasses(!!formState.errors.website), 'w-full')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='order_number'
                  render={({ field, formState }) => (
                    <FormItem className={cn('mb-[20px]', form.watch('website') === '7' ? 'hidden' : '')}>
                      <FormLabel
                        className={cn(
                          labelClasses,
                          classes.requiredLabel,
                          'flex flex-col items-start lg_flex-row lg_items-center',
                        )}
                      >
                        <div className='mr-[10px]'>Order Number</div>

                        <div className='font-roboto-regular text-[14px] text-[#4C4C4C]'>
                          (Please enter your order number, not your warranty card number.)
                        </div>
                      </FormLabel>

                      <FormControl>
                        <Input
                          className={getInputClasses(!!formState.errors.order_number)}
                          placeholder={
                            form.watch('website')
                              ? orderFormats[form.watch('website')].placeholder
                              : orderFormats['4'].placeholder
                          }
                          {...field}
                          id='order_number'
                          autoComplete='order_number'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='purchased_item_category'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={cn(labelClasses, classes.requiredLabel, 'mb-[10px]')}>
                        What products did you buy from us?
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className='flex h-fit items-start gap-4'
                        >
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='1'
                              id='category_1'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='category_1'
                            >
                              Boots
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='2'
                              id='category_2'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='category_2'
                            >
                              Waders
                            </Label>
                          </div>
                          {/* <div className='flex items-center space-x-2'>
        <RadioGroupItem2 value='3' id='category_3' />
        <Label htmlFor='category_3'>Accessories</Label>
      </div> */}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <h2 className={cn(bigLabelClasses, 'lg_mt-[40px]')}>Preferences (3/3)</h2>
                <FormField
                  control={form.control}
                  name='usage'
                  render={({ field }) => (
                    <FormItem className='mb-[20px]'>
                      <FormLabel className={cn(labelClasses, classes.requiredLabel)}>
                        What do you mainly use our products for?
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className='flex h-fit flex-col items-start lg_h-[26px] lg_flex-row lg_items-center lg_gap-[100px]'
                        >
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='1'
                              id='usage_1'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='usage_1'
                            >
                              Farming
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='2'
                              id='usage_2'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='usage_2'
                            >
                              Gardening
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='3'
                              id='usage_3'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='usage_3'
                            >
                              Hunting
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='4'
                              id='usage_4'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='usage_4'
                            >
                              Outdoor works
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='5'
                              id='usage_5'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='usage_5'
                            >
                              Fishing
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='platform'
                  render={({ field }) => (
                    <FormItem className='mb-[20px]'>
                      <FormLabel className={cn(labelClasses, classes.requiredLabel)}>
                        What social platform do you use most?
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={value => {
                            field.onChange(value);
                          }}
                          value={field.value}
                          className='flex h-fit flex-col items-start lg_h-[26px] lg_flex-row lg_items-center lg_gap-[100px]'
                        >
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='1'
                              id='platform_1'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='platform_1'
                            >
                              Facebook
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='2'
                              id='platform_2'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='platform_2'
                            >
                              Instagram
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='3'
                              id='platform_3'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='platform_3'
                            >
                              YouTube
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='4'
                              id='platform_4'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='platform_4'
                            >
                              Tiktok
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='5'
                              id='platform_5'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='platform_5'
                            >
                              Pinterest
                            </Label>
                          </div>
                          <div className='flex items-center space-x-2'>
                            <RadioGroupItem2
                              value='6'
                              id='platform_6'
                            />
                            <Label
                              className={labelClasses}
                              htmlFor='platform_6'
                            >
                              Others
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='platform_other'
                  render={({ field, formState }) => (
                    <FormItem className={cn(form.watch('platform') === '6' ? 'block' : 'hidden')}>
                      <FormLabel className={cn(labelClasses, classes.requiredLabel)}>
                        If Others, please specify
                      </FormLabel>
                      <FormControl>
                        <Input
                          className={getInputClasses(!!formState.errors.platform)}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='mt-[40px]'>
                <Button
                  type='submit'
                  className='duration-300 h-[40px] w-full rounded-full bg-black font-roboto-medium text-[16px] leading-[40px] text-white transition-colors hover_bg-[#4C4C4C] lg_h-[50px] lg_w-[140px] lg_text-[18px] lg_leading-[50px]'
                >
                  {isLoading ? 'loading...' : 'Send'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}

      {openMaskDialog && (
        <Portal>
          <aside className='fixed left-1/2 top-1/2 z-[100] max-h-[90vh] w-[calc(100vw-36px)] -translate-x-1/2 -translate-y-1/2 transform overflow-y-auto bg-white lg_w-[640px]'>
            <div className='flex h-[46px] items-center justify-end pr-[20px]'>
              <button
                className='flex h-[46px] w-[46px] cursor-pointer items-center justify-center'
                onClick={() => setOpenMaskDialog(false)}
              >
                <CloseIcon
                  size={30}
                  strokeWidth={1.5}
                />
              </button>
            </div>
            <div className='px-[24px] pb-[46px] text-center lg_px-[70px]'>
              {/* 盾牌图标 */}
              <img
                className='mx-auto mb-[20px]'
                src='/warranty-page/vector.png'
                alt='Warranty Active'
                width={50}
                height={56}
              />

              {/* 标题: R/Medium/20 weight:500 line-height:28px color:#000 */}
              <h2 className='mb-[16px] font-roboto-medium text-[16px] font-medium leading-[24px] text-[#000] lg_text-[20px] lg_leading-[28px]'>
                YOUR LIFETIME WARRANTY IS ACTIVE!
              </h2>

              {/* 描述: R/Light/16 weight:300 line-height:26px color:#000 max-width:494px */}
              <p className='mx-auto mb-[24px] max-w-[494px] font-roboto-light text-[14px] font-light leading-[22px] text-[#000] lg_text-[16px] lg_leading-[26px]'>
                Proper care & maintenance of your HISEA gear are essential to ensure their longevity and optimal
                performance.
              </p>

              {/* 副标题: R/Medium/18 weight:500 line-height:100% color:#000 */}
              <h3 className='mb-[24px] font-roboto-medium text-[16px] font-medium leading-[100%] text-[#000] lg_text-[18px]'>
                Your member welcome pack is ready:
              </h3>

              {/* 两张卡片 */}
              <div className='mb-[24px] flex items-stretch justify-center gap-[12px]'>
                {/* 卡片01 */}
                <div className='flex flex-1 flex-col items-center rounded-[8px] bg-[#F9F9F9] px-[8px] pb-[10px] pt-[6px] lg_w-[200px] lg_flex-none'>
                  <span className='font-teko-medium text-[32px] font-medium uppercase leading-[38px] text-[#E0E0E0] lg_text-[40px] lg_leading-[46px]'>
                    01
                  </span>
                  {/* R/Regular/14 weight:400 line-height:100% color:#000 */}
                  <p className='font-roboto-regular text-[12px] leading-[140%] text-[#000] lg_text-[14px]'>
                    Your $10 official site
                    <br />
                    discount code:
                  </p>
                  <span className='mt-auto inline-flex h-[27px] w-[110px] items-center justify-center bg-[#E8553D] font-roboto-medium text-[14px] font-medium tracking-[1px] text-white'>
                    REORDER10
                  </span>
                </div>

                {/* 卡片02 */}
                <div className='flex flex-1 flex-col items-center rounded-[8px] bg-[#F9F9F9] px-[8px] pb-[10px] pt-[6px] lg_w-[200px] lg_flex-none'>
                  <span className='font-teko-medium text-[32px] font-medium uppercase leading-[38px] text-[#E0E0E0] lg_text-[40px] lg_leading-[46px]'>
                    02
                  </span>
                  {/* R/Regular/14 weight:400, span(care guide.) weight:600 uppercase */}
                  <p className='font-roboto-regular text-[12px] leading-[140%] text-[#000] lg_text-[14px]'>
                    Boots & waders
                    <br />
                    <span className='font-semibold uppercase'>care guide.</span>
                  </p>
                  <LinkButton
                    href='/care-guide'
                    className='mt-auto inline-flex h-[27px] w-[110px] items-center justify-center bg-[#000] font-roboto-medium text-[14px] font-medium tracking-[1px] text-white transition-colors hover_bg-[#333]'
                  >
                    READ
                  </LinkButton>
                </div>
              </div>

              {/* 底部提示: R/Light/14 weight:300 color:#000 */}
              <p className='font-roboto-regular text-[12px] leading-[100%] text-[#4C4C4C] lg_text-[14px]'>
                Please also check your email.
              </p>
            </div>
          </aside>
          <div
            className='fixed left-0 top-0 z-[99] h-[100vh] w-[100vw] bg-black opacity-60'
            onClick={() => setOpenMaskDialog(false)}
          ></div>
        </Portal>
      )}
      {/* <button
        type='button'
        onClick={() => setOpenMaskDialog(true)}
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
          padding: '8px 16px',
          background: '#e74c3c',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
        }}
      >
        调试：显示成功弹窗
      </button> */}

      {/* <Dialog
        open={openMaskDialog}
        onOpenChange={setOpenMaskDialog}
      >
        <DialogContent
          className='max-w-[96vw] text-center lg_max-w-[600px] lg_p-[40px]'
          closeClassName='absolute top-[-2rem] right-0 text-white'
          xClassName='text-white w-[30px] h-[30px]'
        >
          <div className='flex flex-col items-center justify-center gap-[18px]'>
            {submitSuccess ? (
              <CircleCheckBig className='h-[30px] w-[30px] text-primary' />
            ) : (
              <BadgeX className='h-[30px] w-[30px] text-primary' />
            )}

            <p className='text-[18px]'>{responseInfo}</p>
          </div>

          <LinkButton
            href='/'
            className={cn('mt-[30px] h-[50px] w-full font-oswald-bold uppercase', submitSuccess ? '' : 'hidden')}
          >
            back to shopping
          </LinkButton>
        </DialogContent>
      </Dialog> */}
    </div>
  );
};
